import React, { useState, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Calendar, Clock, MapPin, Monitor, User, ChevronRight, GraduationCap, ChevronLeft } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { UpcomingSession } from '@/services/dashboard';
import { useNavigate } from 'react-router-dom';

interface UpcomingSessionsWidgetProps {
  sessions: UpcomingSession[];
  loading?: boolean;
}

const UpcomingSessionsWidget = ({
  sessions,
  loading = false
}: UpcomingSessionsWidgetProps) => {
  const navigate = useNavigate();
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const cardsPerView = 4; // Número de cards visíveis por vez
  const maxIndex = Math.max(0, sessions.length - cardsPerView);

  const scrollToIndex = (index: number) => {
    if (scrollContainerRef.current) {
      const cardWidth = scrollContainerRef.current ? scrollContainerRef.current.clientWidth / 4 : 0; // Largura do container dividido por 4
      scrollContainerRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
    }
    setCurrentIndex(index);
  };

  const handlePrevious = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = Math.min(maxIndex, currentIndex + 1);
    scrollToIndex(newIndex);
  };

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'onlineSync':
        return <Monitor className="h-2.5 w-2.5" />;
      case 'in-person':
        return <MapPin className="h-2.5 w-2.5" />;
      default:
        return <Calendar className="h-2.5 w-2.5" />;
    }
  };

  const getModeLabel = (mode: string) => {
    switch (mode) {
      case 'onlineSync':
        return 'Online';
      case 'in-person':
        return 'Presencial';
      default:
        return mode;
    }
  };

  const getInitials = (name: string) => {
    if (!name) return 'C';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Function to handle image loading errors
  const handleImageError = (sessionId: string) => {
    setFailedImages(prev => ({
      ...prev,
      [sessionId]: true
    }));
  };

  const getCustomerData = (session: UpcomingSession) => {
    const customer = session.class.subscription?.subscription_customers?.[0]?.customer;
    return {
      name: customer?.name || 'Cliente não identificado',
      domain: customer?.domain || null
    };
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <div className="flex gap-3 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="w-[200px] h-[100px] bg-gray-200 rounded-lg flex-shrink-0"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {sessions.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground flex-1 flex flex-col justify-center">
            <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Nenhuma sessão agendada</p>
          </div>
        ) : (
          <div className="relative flex-1">
            {/* Navigation Buttons */}
            {sessions.length > cardsPerView && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 h-8 w-8 p-0 rounded-full bg-white shadow-md border-gray-200 hover:bg-gray-50"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 h-8 w-8 p-0 rounded-full bg-white shadow-md border-gray-200 hover:bg-gray-50"
                  onClick={handleNext}
                  disabled={currentIndex >= maxIndex}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            
            {/* Cards Container */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-3 overflow-x-auto h-full items-center px-1"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none'
              }}
            >
              {sessions.map((session) => {
                const customerData = getCustomerData(session);
                const instructorName = session.instructor 
                  ? `${session.instructor.first_name} ${session.instructor.last_name}`
                  : 'Instrutor não definido';
                
                return (
                  <div
                    key={session.id}
                    className="group relative p-2 rounded-lg border border-gray-100 bg-white hover:border-blue-200 hover:shadow-md cursor-pointer transition-all duration-200 w-[200px] flex-shrink-0 h-[100px]"
                    onClick={() => navigate(`/classes/${session.class.id}`)}
                  >
                    <div className="flex items-start gap-2 h-full">
                      {/* Customer Avatar */}
                      <Avatar className="h-6 w-6 border border-gray-100 group-hover:border-blue-200 transition-colors flex-shrink-0">
                        {customerData.domain && !failedImages[session.id] ? (
                          <AvatarImage 
                            src={`https://cdn.brandfetch.io/${customerData.domain}?c=1idk4AUs2-buvooOUvQ`} 
                            alt={customerData.name}
                            onError={() => handleImageError(session.id)}
                          />
                        ) : (
                          <AvatarFallback className="bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 font-extrabold text-[8px]">
                            {getInitials(customerData.name)}
                          </AvatarFallback>
                        )}
                      </Avatar>

                      {/* Session Details */}
                      <div className="flex-1 min-w-0 space-y-1">
                        {/* Course Name and Customer */}
                        <div>
                          <h4 className="font-extrabold text-gray-900 text-xs leading-tight group-hover:text-blue-700 transition-colors line-clamp-1">
                            {session.class.course.name}
                          </h4>
                          <p className="text-[10px] text-gray-500 truncate">
                            {customerData.name}
                          </p>
                        </div>

                        {/* Compact Info Row - Date/Time and Mode on same line */}
                        <div className="flex items-center gap-3 text-[10px] text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-2.5 w-2.5 text-gray-400" />
                            <span>{format(parseISO(session.start_date), "dd/MM 'às' HH:mm", { locale: ptBR })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {getModeIcon(session.mode)}
                            <span>{getModeLabel(session.mode)}</span>
                          </div>
                        </div>

                        {/* Instructor */}
                        <div className="flex items-center gap-1 text-[10px] text-gray-600">
                          <User className="h-2.5 w-2.5 text-gray-400" />
                          <span className="truncate">{instructorName}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingSessionsWidget;
